import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  updateProfile,
} from 'firebase/auth';

const TABS = [
  { id: 'editProfile', label: 'Edit Profile' },
  { id: 'changePassword', label: 'Change Password' },
  { id: 'notifications', label: 'Notification Preferences' },
];

export default function Settings({ onClose, initialTab, currentUser, userProfile, onProfileUpdated }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'editProfile');

  // Edit Profile
  const [name, setName] = useState(userProfile?.name || '');
  const [handicap, setHandicap] = useState(userProfile?.handicap ?? '');
  const [phone, setPhone] = useState(userProfile?.phone || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Change Password
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  // Notification Prefs
  const defaultPrefs = { roundApproved: true, roundDisputed: true, newEvent: true, positionChange: true };
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (snap.exists() && snap.data().notificationPrefs) {
          setPrefs({ ...defaultPrefs, ...snap.data().notificationPrefs });
        }
      } catch (e) { console.error(e); }
    };
    loadPrefs();
  }, [currentUser.uid]);

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        name: name.trim(),
        handicap: parseFloat(handicap) || 0,
        phone: phone.trim(),
        updatedAt: serverTimestamp(),
      });
      await updateProfile(currentUser, { displayName: name.trim() });
      onProfileUpdated();
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (e) {
      setProfileMsg({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdMsg(null);
    if (newPwd !== confirmPwd) {
      setPwdMsg({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    if (newPwd.length < 6) {
      setPwdMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    setPwdSaving(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPwd);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPwd);
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      setPwdMsg({ type: 'success', text: 'Password changed successfully.' });
    } catch (e) {
      const map = {
        'auth/wrong-password': 'Current password is incorrect.',
        'auth/weak-password': 'Password must be at least 6 characters.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/invalid-credential': 'Current password is incorrect.',
      };
      setPwdMsg({ type: 'error', text: map[e.code] || 'Something went wrong. Please try again.' });
    } finally {
      setPwdSaving(false);
    }
  };

  const handleTogglePref = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { notificationPrefs: updated });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) { console.error(e); }
  };

  const prefItems = [
    { key: 'roundApproved', label: 'Round approved notifications' },
    { key: 'roundDisputed', label: 'Round disputed notifications' },
    { key: 'newEvent', label: 'New event notifications' },
    { key: 'positionChange', label: 'Position change notifications' },
  ];

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={s.header}>
          <span style={s.title}>Settings</span>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={s.tabBar}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={s.body}>
          {activeTab === 'editProfile' && (
            <div style={s.section}>
              <label style={s.label}>Display Name</label>
              <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
              <label style={s.label}>Handicap</label>
              <input style={s.input} type="number" min="0" max="54" step="0.1" value={handicap} onChange={e => setHandicap(e.target.value)} placeholder="0.0" />
              <label style={s.label}>Phone Number <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
              <input style={s.input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+32 ..." />
              {profileMsg && <div style={profileMsg.type === 'success' ? s.msgSuccess : s.msgError}>{profileMsg.text}</div>}
              <button style={s.btn} onClick={handleSaveProfile} disabled={profileSaving}>
                {profileSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          )}

          {activeTab === 'changePassword' && (
            <div style={s.section}>
              <label style={s.label}>Current Password</label>
              <input style={s.input} type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} placeholder="Current password" />
              <label style={s.label}>New Password</label>
              <input style={s.input} type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Min. 6 characters" />
              <label style={s.label}>Confirm New Password</label>
              <input style={s.input} type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} placeholder="Repeat new password" />
              {pwdMsg && <div style={pwdMsg.type === 'success' ? s.msgSuccess : s.msgError}>{pwdMsg.text}</div>}
              <button style={s.btn} onClick={handleChangePassword} disabled={pwdSaving}>
                {pwdSaving ? 'Saving…' : 'Change Password'}
              </button>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div style={s.section}>
              {prefItems.map(item => (
                <div key={item.key} style={s.toggleRow} onClick={() => handleTogglePref(item.key)}>
                  <span style={s.toggleLabel}>{item.label}</span>
                  <div style={{ ...s.toggleTrack, backgroundColor: prefs[item.key] ? '#d4af37' : '#374151' }}>
                    <div style={{ ...s.toggleThumb, transform: prefs[item.key] ? 'translateX(20px)' : 'translateX(2px)' }} />
                  </div>
                </div>
              ))}
              {savedFlash && <div style={s.msgSuccess}>Saved ✓</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: '16px',
  },
  modal: {
    backgroundColor: '#1e1e1e', border: '1px solid #2d2d2d', borderRadius: '16px',
    width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px 0', marginBottom: '0',
  },
  title: { fontSize: '18px', fontWeight: '700', color: '#ffffff' },
  closeBtn: {
    background: 'none', border: 'none', color: '#9ca3af', fontSize: '18px',
    cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
  },
  tabBar: {
    display: 'flex', gap: '4px', padding: '16px 24px 0', borderBottom: '1px solid #2d2d2d',
    overflowX: 'auto',
  },
  tab: {
    padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '500', color: '#9ca3af', borderRadius: '6px 6px 0 0',
    whiteSpace: 'nowrap', marginBottom: '-1px', borderBottom: '2px solid transparent',
    transition: 'color 0.2s ease',
  },
  tabActive: { color: '#d4af37', borderBottom: '2px solid #d4af37' },
  body: { padding: '24px' },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#9ca3af', marginBottom: '-6px' },
  input: {
    padding: '10px 14px', backgroundColor: '#0f0f0f', border: '1px solid #2d2d2d',
    borderRadius: '8px', color: '#ffffff', fontSize: '14px', outline: 'none',
    fontFamily: 'Inter, sans-serif',
  },
  btn: {
    padding: '12px', backgroundColor: '#d4af37', color: '#0f0f0f', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    marginTop: '4px',
  },
  msgSuccess: { fontSize: '13px', color: '#10b981', fontWeight: '500' },
  msgError: { fontSize: '13px', color: '#ef4444', fontWeight: '500' },
  toggleRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', backgroundColor: '#0f0f0f', borderRadius: '10px',
    border: '1px solid #2d2d2d', cursor: 'pointer',
  },
  toggleLabel: { fontSize: '14px', color: '#ffffff', fontWeight: '500' },
  toggleTrack: {
    width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
    transition: 'background-color 0.2s ease', flexShrink: 0,
  },
  toggleThumb: {
    position: 'absolute', top: '2px', width: '20px', height: '20px',
    borderRadius: '50%', backgroundColor: '#ffffff', transition: 'transform 0.2s ease',
  },
};
